import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';

/**
 * Links a portal site to the git repository that holds its marketing website
 * (docs/claude/website-editor). One website per portal site.
 *
 * The CONTENT of the website never lives here — it lives in the repository (decision D1).
 * Deliberately absent too: branch names, preview hostnames and git credentials. Those come
 * from environment configuration (`websites.*`), so a database row — including one cloned
 * from production into staging — can never widen what an environment is allowed to write.
 */
// A brand may have SEVERAL websites — its main marketing site, a campaign landing page an
// agency stood up, a sub-site — so the portal site is indexed, not unique. `siteKey` stays
// unique where present; it names the repository and hosting project, so only a site Workd
// builds has one.
@Index('fk_portal_site__id', ['fkPortalSiteId'], {})
@Index('UK_website_siteKey', ['siteKey'], { unique: true })
@Entity('website')
export class WebsiteEntity extends BaseEntity {
	@Column('int', { name: 'fk_portal_site__id', unsigned: true })
	public fkPortalSiteId!: number;

	// What to call this website in Workd. With several per brand, the brand's name no longer
	// identifies one ("Capital Trading marketing site", "Spring campaign landing page").
	@Column('varchar', { name: 'name', length: 120, default: '' })
	public name!: string;

	/**
	 * `<brand>--<tenant>` (for example `ctut--hf`), set only for a website Workd BUILDS: it
	 * names the repository, the hosting project and the preview hostname, and it is what the
	 * content editor works against. Written once and never changed, even if the portal site
	 * alias is later renamed.
	 *
	 * Null for a website Workd merely recognizes — an agency's landing page, a customer's
	 * existing site — which has no repository and is not offered the editor.
	 */
	@Column('varchar', { name: 'siteKey', nullable: true, length: 96 })
	public siteKey!: string | null;

	// Which GitProvider implementation serves this site. 'github' today, null with no repository.
	@Column('varchar', {
		name: 'gitProvider',
		nullable: true,
		length: 32,
		default: 'github',
	})
	public gitProvider!: string | null;

	@Column('varchar', { name: 'repoOwner', nullable: true, length: 100 })
	public repoOwner!: string | null;

	@Column('varchar', { name: 'repoName', nullable: true, length: 100 })
	public repoName!: string | null;

	@Column('varchar', { name: 'productionUrl', nullable: true, length: 255 })
	public productionUrl!: string | null;

	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	// Diagnostics only: the contract version last seen in the repository's workd.site.yml.
	@Column('int', { name: 'schemaVersion', nullable: true, unsigned: true })
	public schemaVersion!: number | null;

	@Column('datetime', { name: 'schemaCheckedAt', nullable: true })
	public schemaCheckedAt!: Date | null;

	@ManyToOne(() => PortalSiteEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public portalSite!: PortalSiteEntity;
}
